import { useGetIntegration, useUpdateIntegration } from 'entities/campaign-integrations';
import type { GetCampaignIntegrationModel } from 'entities/campaign-integrations';
import { Button } from 'shared/ui/Button';
import { AlertModal, SideModal, SideModalBody, SideModalFooter, useModalsActions } from 'shared/ui/SideModal';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { Switcher } from 'shared/ui/Switcher';
import { useMemo } from 'react';

import { DeleteIntegration } from './DeleteIntegration';
import { IntegrationFieldsList, getIntegrationFieldsFromArray } from './IntegrationFieldsList';
import { convertMetaToData, getArrayForIntegrationsFieldsList, useIntegrationFields } from './useIntegrationFields';

interface EditIntegrationModalProps {
  integrationId: number;
  campaignId: number;
}

export function EditIntegrationModal({ integrationId, campaignId }: EditIntegrationModalProps) {
  const getIntegrationQuery = useGetIntegration(integrationId);

  return (
    <SideModal
      autoTestAttributesName="edit-integration-modal"
      isLoading={getIntegrationQuery.isLoading}
    >
      {getIntegrationQuery.data && (
        <EditIntegrationModalBody
          campaignId={campaignId}
          integrationId={integrationId}
          integration={getIntegrationQuery.data.data}
        />
      )}
    </SideModal>
  );
}

interface EditIntegrationModalBodyProps extends EditIntegrationModalProps {
  integration: GetCampaignIntegrationModel;
}

export function EditIntegrationModalBody({ integration, integrationId, campaignId }: EditIntegrationModalBodyProps) {
  const { forceClose } = useModalsActions();
  const integrationFields = useMemo(() => getArrayForIntegrationsFieldsList(integration.fields), [integration.fields]);
  const {
    isDirty,
    isDisabled,
    fields,
    doNotActivateOnAdd,
    validate,
    onChange,
    append,
    remove,
    errors,
    setDoNotActivateOnAdd,
    hasMeta,
  } = useIntegrationFields({
    integrationFields,
    integrationAlias: integration.integration.alias,
    isActive: integration.isActive,
  });

  const updateIntegrationQuery = useUpdateIntegration(campaignId, integrationId);
  const submit = () => {
    const { hasError } = validate();

    if (!hasError) {
      updateIntegrationQuery.mutate(
        {
          payload: {
            isActive: doNotActivateOnAdd,
            fields: getIntegrationFieldsFromArray(hasMeta ? convertMetaToData(fields) : fields),
          },
        },
        { onSuccess: forceClose }
      );
    }
  };

  return (
    <>
      <SideModalBody title="Edit integration">
        <form onSubmit={submit}>
          <AlertModal
            autoTestAttributesName="edit-integration-exit-modal"
            showCondition={isDirty}
          />
          <div className="mb-4 flex items-center justify-between">
            <div className="text-sm">{integration.integration.label}</div>
            <Switcher
              name="isActive"
              checked={doNotActivateOnAdd}
              onChange={setDoNotActivateOnAdd}
            />
          </div>
          <IntegrationFieldsList
            fields={fields}
            errors={errors}
            onChange={onChange}
            append={append}
            remove={remove}
          />
          <div className="mt-8 border-t border-slate-100 py-8">
            <DeleteIntegration
              campaignId={campaignId}
              integrationId={integrationId}
            />
          </div>
        </form>
      </SideModalBody>
      <SideModalFooter>
        <Button
          className="w-full"
          disabled={isDisabled}
          loading={updateIntegrationQuery.isLoading}
          onClick={submit}
          {...getDataAutoTestAttributes(['button-save-edit-integration'])}
        >
          Save changes
        </Button>
      </SideModalFooter>
    </>
  );
}
